import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  Activity, Plus, Trash2, TrendingUp, TrendingDown, Minus,
  Scale, Heart, Moon, Footprints, Ruler, Droplets, ChevronRight, Target
} from "lucide-react";
import { format, parseISO, subDays } from "date-fns";
import { utcEntryDay } from "@/lib/trackerx-dates";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from "recharts";
import { GoalDialog, FITNESS_METRICS, LOWER_IS_BETTER, type FitnessMetricKey, type DialogState } from "./fitness-goal";
import type { Goal } from "@shared/schema";
import ShareDialog from "@/components/share-dialog";
import { Share2 } from "lucide-react";
import { isFutureGoal } from "@/lib/goal-visibility";

interface HealthLog {
  id: string;
  date: string;
  metricType: string;
  value: number;
  unit: string;
  notes?: string;
  // Typed columns returned by GET /api/health/logs
  bloodSugar?: number | null;
  caloriesBurned?: number | null;
  heightCm?: number | null;
  chestCm?: number | null;
  waistCm?: number | null;
  hipsCm?: number | null;
  armsCm?: number | null;
  thighsCm?: number | null;
  // Legacy typed columns (older metrics)
  weightKg?: number | null;
  bodyFatPct?: number | null;
  systolicBp?: number | null;
  diastolicBp?: number | null;
  restingHeartRate?: number | null;
  sleepHours?: number | null;
  stepsCount?: number | null;
}

/**
 * Maps metricType → the typed column that holds the display-unit value.
 * "weight" is intentionally excluded: weightKg stores SI kg but the chart
 * unit is lbs, so we must keep reading `value` (lbs) for weight tiles.
 */
const TYPED_FIELD_MAP: Partial<Record<string, keyof HealthLog>> = {
  blood_sugar:        "bloodSugar",
  calories_burned:    "caloriesBurned",
  height:             "heightCm",
  chest:              "chestCm",
  waist:              "waistCm",
  hips:               "hipsCm",
  arms:               "armsCm",
  thighs:             "thighsCm",
  body_fat:           "bodyFatPct",
  blood_pressure_sys: "systolicBp",
  blood_pressure_dia: "diastolicBp",
  heart_rate:         "restingHeartRate",
  sleep_hours:        "sleepHours",
  steps:              "stepsCount",
};

/**
 * Returns the display-unit value for a health log entry.
 * Reads from the typed column when it is non-null (primary source),
 * falling back to the generic `value` column for backward compatibility.
 */
function resolveValue(log: HealthLog): number {
  const typedKey = TYPED_FIELD_MAP[log.metricType];
  if (typedKey) {
    const typed = log[typedKey];
    if (typed != null) return typed as number;
  }
  return log.value;
}

const METRIC_GROUPS = {
  body: {
    label: "Body",
    icon: Scale,
    metrics: [
      { type: "weight", label: "Weight", unit: "lbs", icon: "⚖️", color: "#6366f1", decimals: 1 },
      { type: "body_fat", label: "Body Fat %", unit: "%", icon: "📊", color: "#f59e0b", decimals: 1 },
      { type: "chest", label: "Chest", unit: "in", icon: "📏", color: "#10b981", decimals: 1 },
      { type: "waist", label: "Waist", unit: "in", icon: "📏", color: "#f43f5e", decimals: 1 },
      { type: "hips", label: "Hips", unit: "in", icon: "📏", color: "#8b5cf6", decimals: 1 },
      { type: "arms", label: "Arms", unit: "in", icon: "💪", color: "#06b6d4", decimals: 1 },
      { type: "thighs", label: "Thighs", unit: "in", icon: "📏", color: "#84cc16", decimals: 1 },
      { type: "height", label: "Height", unit: "in", icon: "📐", color: "#64748b", decimals: 1 },
    ],
  },
  vitals: {
    label: "Vitals",
    icon: Heart,
    metrics: [
      { type: "blood_pressure_sys", label: "Blood Pressure (Sys)", unit: "mmHg", icon: "❤️", color: "#ef4444", decimals: 0 },
      { type: "blood_pressure_dia", label: "Blood Pressure (Dia)", unit: "mmHg", icon: "💙", color: "#3b82f6", decimals: 0 },
      { type: "heart_rate", label: "Resting Heart Rate", unit: "bpm", icon: "💓", color: "#f43f5e", decimals: 0 },
      { type: "blood_sugar", label: "Blood Sugar", unit: "mg/dL", icon: "🩸", color: "#f59e0b", decimals: 0 },
    ],
  },
  activity: {
    label: "Activity",
    icon: Activity,
    metrics: [
      { type: "steps", label: "Steps", unit: "steps", icon: "👟", color: "#10b981", decimals: 0 },
      { type: "sleep_hours", label: "Sleep", unit: "hrs", icon: "😴", color: "#8b5cf6", decimals: 1 },
      { type: "calories_burned", label: "Calories Burned", unit: "kcal", icon: "🔥", color: "#f97316", decimals: 0 },
    ],
  },
};

const ALL_METRICS = Object.values(METRIC_GROUPS).flatMap(g => g.metrics);

// Maps health metric type → fitness goal key
const HEALTH_TO_GOAL_METRIC: Partial<Record<string, FitnessMetricKey>> = {
  weight: "weight",
  body_fat: "bodyFat",
  chest: "chestCm",
  waist: "waistCm",
  hips: "hipsCm",
  arms: "armsCm",
  thighs: "thighsCm",
  height: "heightCm",
  heart_rate: "heartRate",
  blood_sugar: "bloodSugar",
  steps: "steps",
  sleep_hours: "sleepHours",
  calories_burned: "caloriesBurned",
};

// Conversion factors from health-metrics display units to fitness-goal target units
// Health metrics use imperial (lbs, in); fitness goals use metric (kg, cm)
const HEALTH_TO_GOAL_UNIT_FACTOR: Partial<Record<string, number>> = {
  weight: 0.453592,  // lbs → kg
  chest: 2.54,       // in → cm
  waist: 2.54,
  hips: 2.54,
  arms: 2.54,
  thighs: 2.54,
  height: 2.54,
};

function getTrend(logs: HealthLog[], metricType: string): { pct: number; dir: "up" | "down" | "flat" } {
  const typeLogs = logs.filter(l => l.metricType === metricType).slice(0, 7).reverse();
  if (typeLogs.length < 2) return { pct: 0, dir: "flat" };
  const first = resolveValue(typeLogs[0]);
  const last = resolveValue(typeLogs[typeLogs.length - 1]);
  const diff = ((last - first) / Math.max(first, 0.001)) * 100;
  return { pct: Math.abs(diff), dir: diff > 0.5 ? "up" : diff < -0.5 ? "down" : "flat" };
}

function getLatest(logs: HealthLog[], metricType: string): HealthLog | null {
  return logs.find(l => l.metricType === metricType) || null;
}

function MetricCard({
  metric, logs, onClick
}: {
  metric: typeof ALL_METRICS[0];
  logs: HealthLog[];
  onClick: () => void;
}) {
  const latest = getLatest(logs, metric.type);
  const trend = getTrend(logs, metric.type);
  const chartData = logs.filter(l => l.metricType === metric.type).slice(0, 14).reverse()
    .map(l => ({ date: format(parseISO(utcEntryDay(l.date)), "M/d"), value: resolveValue(l) }));

  return (
    <button className="bg-card border border-border rounded-xl p-4 text-left hover:border-primary/50 transition-colors w-full"
      onClick={onClick}>
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-base">{metric.icon}</span>
            <span className="text-sm font-medium text-muted-foreground">{metric.label}</span>
          </div>
          <div className="text-2xl font-bold mt-1">
            {latest ? resolveValue(latest).toFixed(metric.decimals) : "—"}
            {latest && <span className="text-sm font-normal text-muted-foreground ml-1">{metric.unit}</span>}
          </div>
        </div>
        {trend.dir !== "flat" && (
          <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
            trend.dir === "up" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
          }`}>
            {trend.dir === "up" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend.pct.toFixed(1)}%
          </div>
        )}
      </div>
      {chartData.length > 1 && (
        <div className="h-14 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 2, right: 4, bottom: 2, left: 4 }}>
              <Line type="monotone" dataKey="value" stroke={metric.color} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {!latest && (
        <div className="text-xs text-muted-foreground mt-2">No data yet — click to log</div>
      )}
    </button>
  );
}

function MetricDetailPanel({
  metric, logs, onAdd, onDelete, onShare, goals, onSetGoal
}: {
  metric: typeof ALL_METRICS[0] | null;
  logs: HealthLog[];
  onAdd: (data: { metricType: string; value: number; date: string; unit: string }) => void;
  onDelete: (id: string) => void;
  onShare: (log: HealthLog) => void;
  goals: Goal[];
  onSetGoal: (metricType: string, existingGoal: Goal | null) => void;
}) {
  const [value, setValue] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));

  if (!metric) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Select a metric to view details and log data</p>
        </div>
      </div>
    );
  }

  const metricLogs = logs.filter(l => l.metricType === metric.type).slice(0, 60).reverse();
  const chartData = metricLogs.map(l => ({ date: format(parseISO(utcEntryDay(l.date)), "M/d"), value: resolveValue(l) }));
  const latest = getLatest(logs, metric.type);
  const trend = getTrend(logs, metric.type);

  const goalKey = HEALTH_TO_GOAL_METRIC[metric.type];
  const existingGoal = goalKey
    ? goals.find(g => (g as any).goalSource === "fitness" && (g as any).healthMetric === goalKey) ?? null
    : null;
  const goalTarget = existingGoal?.customGoalTarget ?? null;
  const lowerIsBetter = goalKey ? LOWER_IS_BETTER.has(goalKey) : false;
  const goalMetricInfo = goalKey ? FITNESS_METRICS.find(m => m.key === goalKey) : null;

  // Convert the logged value to the goal's unit for progress comparison
  // (health logs may use imperial: lbs/inches; goal targets are stored in metric: kg/cm)
  const conversionFactor = HEALTH_TO_GOAL_UNIT_FACTOR[metric.type] ?? 1;
  const latestInGoalUnit = latest ? latest.value * conversionFactor : null;

  let goalPct = 0;
  let goalStatus = "";
  if (latestInGoalUnit !== null && goalTarget !== null && goalTarget > 0) {
    if (lowerIsBetter) {
      goalPct = latestInGoalUnit <= goalTarget ? 100 : Math.max(0, 100 - ((latestInGoalUnit - goalTarget) / goalTarget) * 100);
      goalStatus = latestInGoalUnit <= goalTarget ? "On Track" : "Above target";
    } else {
      goalPct = Math.min(100, (latestInGoalUnit / goalTarget) * 100);
      goalStatus = goalPct >= 90 ? "On Track" : `${goalPct.toFixed(0)}%`;
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <span className="text-2xl">{metric.icon}</span>{metric.label}
          </h2>
          {latest && (
            <p className="text-sm text-muted-foreground mt-1">
              Latest: <strong className="text-foreground">{resolveValue(latest).toFixed(metric.decimals)} {metric.unit}</strong>
              {" "}on {format(parseISO(utcEntryDay(latest.date)), "MMM d, yyyy")}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {trend.dir !== "flat" && (
            <div className={`flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-full ${
              trend.dir === "up" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            }`}>
              {trend.dir === "up" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {trend.pct.toFixed(1)}% last 7 days
            </div>
          )}
          {goalKey && (
            <Button
              variant={existingGoal ? "outline" : "default"}
              size="sm"
              className="flex items-center gap-1.5"
              onClick={() => onSetGoal(metric.type, existingGoal)}
            >
              <Target className="w-3.5 h-3.5" />
              {existingGoal ? "Edit Goal" : "Set Goal"}
            </Button>
          )}
        </div>
      </div>

      {/* Goal progress */}
      {existingGoal && goalTarget !== null && goalMetricInfo && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">{existingGoal.title}</span>
            </div>
            <Badge
              variant={goalStatus === "On Track" ? "default" : goalStatus === "Above target" ? "destructive" : "outline"}
              className={`text-xs ${goalStatus === "On Track" ? "bg-green-500" : ""}`}
            >
              {goalStatus || "No data"}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>Current: <strong className="text-foreground">{latestInGoalUnit !== null ? `${latestInGoalUnit.toFixed(1)} ${goalMetricInfo.unit}` : "—"}</strong></span>
            <span>{lowerIsBetter ? "Target ≤" : "Target:"} <strong className="text-foreground">{goalTarget} {goalMetricInfo.unit}</strong></span>
          </div>
          {latestInGoalUnit !== null && <Progress value={goalPct} className="h-2" />}
        </div>
      )}

      {/* Log new entry */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="font-semibold mb-3">Log {metric.label}</h3>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Value ({metric.unit})</Label>
            <Input type="number" value={value} onChange={e => setValue(e.target.value)}
              placeholder={`Enter ${metric.label.toLowerCase()}...`} className="mt-1" step="any" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1 w-40" />
          </div>
          <Button disabled={!value} onClick={() => {
            onAdd({ metricType: metric.type, value: parseFloat(value), date, unit: metric.unit });
            setValue("");
          }}>
            <Plus className="w-4 h-4 mr-1" />Log
          </Button>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 1 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="font-semibold mb-3">Trend (last 60 entries)</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Line type="monotone" dataKey="value" stroke={metric.color} strokeWidth={2.5}
                  dot={{ fill: metric.color, r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border font-semibold">History</div>
        {metricLogs.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">No entries yet.</div>
        ) : (
          <div>
            {[...metricLogs].reverse().slice(0, 30).map((log) => (
              <div key={log.id} data-testid={`row-health-log-${log.id}`} className="flex items-center justify-between px-4 py-2.5 border-b border-border/50 last:border-0 hover:bg-muted/30">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground w-28">{format(parseISO(utcEntryDay(log.date)), "MMM d, yyyy")}</span>
                  <span className="font-semibold">{resolveValue(log).toFixed(metric.decimals)} {log.unit}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    aria-label="Share entry" data-testid={`button-share-health-log-${log.id}`}
                    onClick={() => onShare(log)}>
                    <Share2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    aria-label="Delete entry" data-testid={`button-delete-health-log-${log.id}`}
                    onClick={() => onDelete(log.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BmiWidget({ logs }: { logs: HealthLog[] }) {
  const weightLog = getLatest(logs, "weight");
  const heightLog = getLatest(logs, "height");
  if (!weightLog || !heightLog) return null;
  const heightM = resolveValue(heightLog) * 0.0254;
  const weightKg = resolveValue(weightLog) * 0.453592;
  const bmi = weightKg / (heightM * heightM);
  const category = bmi < 18.5 ? { label: "Underweight", color: "text-blue-500" }
    : bmi < 25 ? { label: "Normal", color: "text-green-500" }
    : bmi < 30 ? { label: "Overweight", color: "text-yellow-500" }
    : { label: "Obese", color: "text-red-500" };
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="font-semibold mb-1">BMI</h3>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold">{bmi.toFixed(1)}</span>
        <span className={`text-sm font-medium ${category.color}`}>{category.label}</span>
      </div>
      <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "linear-gradient(to right, #3b82f6 0%, #22c55e 25%, #eab308 50%, #ef4444 75%)" }}>
        <div className="h-full w-1 bg-foreground rounded-full transition-all" style={{ marginLeft: `${Math.min(95, Math.max(2, ((bmi - 15) / 25) * 100))}%` }} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>15</span><span>18.5</span><span>25</span><span>30</span><span>40</span>
      </div>
    </div>
  );
}

export default function HealthMetricsPage() {
  const { toast } = useToast();
  const [tab, setTab] = useState("body");
  const [selectedMetric, setSelectedMetric] = useState<typeof ALL_METRICS[0] | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [goalDialog, setGoalDialog] = useState<DialogState>({ open: false, goal: null });
  const [sharingLog, setSharingLog] = useState<HealthLog | null>(null);

  const { data: logs = [] } = useQuery<HealthLog[]>({
    queryKey: ["/api/health/logs"],
    queryFn: async () => {
      const r = await fetch("/api/health/logs?limit=500");
      return r.json();
    },
  });

  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const goals = allGoals.filter(goal => !isFutureGoal(goal));
  const usedMetrics = new Set(
    goals
      .filter(g => (g as any).goalSource === "fitness")
      .map(g => (g as any).healthMetric as string)
      .filter(Boolean)
  );

  const handleSetGoal = (metricType: string, existingGoal: Goal | null) => {
    const goalKey = HEALTH_TO_GOAL_METRIC[metricType];
    if (!goalKey) return;
    setGoalDialog({
      open: true,
      goal: existingGoal,
      initialMetric: goalKey,
    });
  };

  const addLogMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/health/logs", data),
    onMutate: async (newData: { metricType: string; value: number; date: string; unit: string }) => {
      await queryClient.cancelQueries({ queryKey: ["/api/health/logs"] });
      const previousLogs = queryClient.getQueryData<HealthLog[]>(["/api/health/logs"]);
      const optimisticLog: HealthLog = {
        id: `optimistic-${Date.now()}`,
        date: newData.date,
        metricType: newData.metricType,
        value: newData.value,
        unit: newData.unit,
      };
      queryClient.setQueryData<HealthLog[]>(["/api/health/logs"], (old = []) => {
        const filtered = old.filter(
          l => !(l.metricType === newData.metricType && l.date === newData.date)
        );
        return [optimisticLog, ...filtered].sort((a, b) => b.date.localeCompare(a.date));
      });
      return { previousLogs };
    },
    onError: (_err: unknown, _data: unknown, context: { previousLogs?: HealthLog[] } | undefined) => {
      if (context?.previousLogs !== undefined) {
        queryClient.setQueryData(["/api/health/logs"], context.previousLogs);
      }
    },
    onSuccess: () => {
      toast({ title: "Logged successfully" });
      setDetailOpen(false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/health/logs"] });
    },
  });

  const deleteLogMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/health/logs/${id}`),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["/api/health/logs"] });
      const previousLogs = queryClient.getQueryData<HealthLog[]>(["/api/health/logs"]);
      const deletedLog = previousLogs?.find(l => l.id === id);
      queryClient.setQueryData<HealthLog[]>(["/api/health/logs"], (old = []) =>
        old.filter(l => l.id !== id)
      );
      return { previousLogs, deletedLog };
    },
    onError: (_err: unknown, _id: unknown, context: { previousLogs?: HealthLog[]; deletedLog?: HealthLog } | undefined) => {
      if (context?.previousLogs !== undefined) {
        queryClient.setQueryData(["/api/health/logs"], context.previousLogs);
      }
      toast({
        title: "Failed to delete entry",
        description: "The entry was restored. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: (_data: unknown, _id: string, context: { previousLogs?: HealthLog[]; deletedLog?: HealthLog } | undefined) => {
      const deleted = context?.deletedLog;
      if (deleted) {
        toast({
          title: "Entry deleted",
          duration: 6000,
          action: (
            <ToastAction
              altText="Undo delete"
              data-testid="button-undo-delete-log"
              onClick={async () => {
                try {
                  // The API normally upserts by metric+date, which would let
                  // this restore silently overwrite an entry the user logged
                  // for the same day after the delete. skipIfExists makes the
                  // server insert atomically ONLY if no entry exists for that
                  // metric+day, returning { skipped: true } otherwise.
                  const res = await apiRequest("POST", "/api/health/logs", {
                    metricType: deleted.metricType,
                    value: deleted.value,
                    unit: deleted.unit,
                    date: deleted.date,
                    notes: deleted.notes ?? undefined,
                    skipIfExists: true,
                  });
                  const restored: { skipped?: boolean } = await res.json();
                  queryClient.invalidateQueries({ queryKey: ["/api/health/logs"] });
                  if (restored.skipped) {
                    toast({
                      title: "Undo skipped",
                      description:
                        "A newer entry was logged for that metric and day, so the deleted value wasn't restored over it.",
                    });
                  } else {
                    toast({ title: "Entry restored" });
                  }
                } catch {
                  toast({ title: "Couldn't restore entry", variant: "destructive" });
                }
              }}
            >
              Undo
            </ToastAction>
          ),
        });
      }
      // Keep the mobile dialog open while more entries remain for the selected
      // metric so users can delete several entries without reopening it each
      // time. Only close once the last entry for this metric has actually been
      // deleted on the server (a failed delete rolls back and stays open).
      const logs = queryClient.getQueryData<HealthLog[]>(["/api/health/logs"]) ?? [];
      const remaining = logs.filter(l => l.metricType === selectedMetric?.type);
      if (remaining.length === 0) {
        setDetailOpen(false);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/health/logs"] });
    },
  });

  const handleMetricClick = (metric: typeof ALL_METRICS[0]) => {
    setSelectedMetric(metric);
    // The right-side detail panel is always visible at the xl breakpoint, so
    // the full-screen dialog is only needed on smaller viewports.
    const isDesktop = window.matchMedia("(min-width: 1280px)").matches;
    setDetailOpen(!isDesktop);
  };

  const currentGroup = METRIC_GROUPS[tab as keyof typeof METRIC_GROUPS];

  return (
    <div className="fixed inset-0 flex flex-col bg-background overflow-hidden" style={{ left: "var(--sidebar-width, 256px)" }}>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold">Health Metrics</h1>
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="body">Body</TabsTrigger>
              <TabsTrigger value="vitals">Vitals</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Metric cards grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* BMI widget (body tab only) */}
          {tab === "body" && <div className="mb-5"><BmiWidget logs={logs} /></div>}

          <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
            {currentGroup.metrics.map(metric => (
              <MetricCard
                key={metric.type}
                metric={metric}
                logs={logs}
                onClick={() => handleMetricClick(metric)}
              />
            ))}
          </div>

          {/* Quick summary stats */}
          {tab === "activity" && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { type: "steps", goal: 10000, label: "Steps Today", icon: "👟", color: "bg-green-500" },
                { type: "sleep_hours", goal: 8, label: "Sleep (hrs)", icon: "😴", color: "bg-purple-500" },
                { type: "calories_burned", goal: 500, label: "Cal Burned", icon: "🔥", color: "bg-orange-500" },
              ].map(stat => {
                const latest = getLatest(logs.filter(l => utcEntryDay(l.date) === format(new Date(), "yyyy-MM-dd")), stat.type);
                const latestVal = latest ? resolveValue(latest) : null;
                const pct = latestVal != null ? Math.min(100, (latestVal / stat.goal) * 100) : 0;
                return (
                  <div key={stat.type} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{stat.icon}</span>
                      <span className="text-sm text-muted-foreground">{stat.label}</span>
                    </div>
                    <div className="text-2xl font-bold mb-2">{latestVal != null ? latestVal.toFixed(stat.type === "steps" ? 0 : 1) : "—"}<span className="text-sm text-muted-foreground font-normal"> / {stat.goal}</span></div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${stat.color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Detail panel (desktop) */}
        <div className="hidden xl:flex w-96 flex-shrink-0 border-l border-border flex-col overflow-hidden">
          <MetricDetailPanel
            metric={selectedMetric}
            logs={logs}
            onAdd={data => addLogMutation.mutate(data)}
            onDelete={id => deleteLogMutation.mutate(id)}
            onShare={log => setSharingLog(log)}
                  goals={goals}
            onSetGoal={handleSetGoal}
          />
        </div>
      </div>

      {/* Mobile: Metric detail dialog */}
      <Dialog open={detailOpen && !!selectedMetric} onOpenChange={setDetailOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col p-0">
          <div className="flex-1 overflow-y-auto">
            <MetricDetailPanel
              metric={selectedMetric}
              logs={logs}
              onAdd={data => addLogMutation.mutate(data)}
              onDelete={id => deleteLogMutation.mutate(id)}
              onShare={log => setSharingLog(log)}
              goals={goals}
              onSetGoal={handleSetGoal}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Goal creation/edit dialog */}
      {goalDialog.open && (
        <GoalDialog
          state={goalDialog}
          onClose={() => setGoalDialog({ open: false, goal: null })}
          usedMetrics={usedMetrics}
        />
      )}

      {sharingLog && selectedMetric && (
        <ShareDialog
          isOpen={!!sharingLog}
          onClose={() => setSharingLog(null)}
          itemId={sharingLog.id}
          itemType="health_metric"
          itemTitle={`${selectedMetric.label}: ${resolveValue(sharingLog)} ${sharingLog.unit}`}
        />
      )}
    </div>
  );
}
